/* eslint-disable import/no-cycle */
import Axios from 'axios';
import get from 'lodash.get';
import humps from 'humps';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { apiUrl } from 'settings/';
import {
  CartItemInterface,
  DeletedCartItemResponseInterface,
  DeletedCouponResponseInterface,
  DonationAskData,
  NOODLE_CART_SESSION_ID_COOKIE_KEY,
  ShoppingCartCheckoutInterface,
  ShoppingCartInterface,
  ShoppingCartInvoiceInterface,
} from 'commons/types/shoppingCartTypes';
import {
  AddOrRemoveUsersInterface,
  CourseWaitlistInterface,
  CourseWaitlistOperationEnum,
  UsersCourseWaitlistsInterface,
} from 'commons/types/courseWaitlistTypes';
import { Course as CourseType } from 'commons/types/courseTypes';
import { PartnerInstance as PartnerInstanceType } from 'commons/types/partnerInstanceTypes';
import { CreateStripeCourseInvoiceResponse } from 'commons/types/paymentTypes';
import { CustomerInterface, InvoiceInterface } from 'commons/types/stripeTypes';
import { InstructorInterface } from 'commons/types/instructorTypes';
import { MembershipSubscriptionInterface } from 'commons/types/membershipSubscription';
import { Section as SectionType } from 'commons/types/sectionTypes';
import { ContentProviderInterface } from 'commons/types/contentProviderTypes';
import type {
  CourseCategories,
  CourseGroup,
  CreateInviteResponse,
  PartnerInvite,
  TeamUserFlags,
} from 'commons/types';

// Legacy API service imports
import AdminGeneral from './AdminGeneral';
import AdminNoodling from './AdminNoodling';
import AdminPartnerProgram from './AdminPartnerProgram';
import AdminScheduler from './AdminScheduler';
import AdminUser from './AdminUser';
import Bookmark from './Bookmark';
// eslint-disable-next-line import/no-cycle
import Counselor from './Counselor';
import Course from './Course';
import Explore from './Explore';
// eslint-disable-next-line import/no-cycle
import GetStream from './GetStream';
import GlobalSearch from './GlobalSearch';
import Group from './Group';
import LandingPage from './LandingPage';
import Like from './Like';
import Noodling from './Noodling';
import Onboarding from './Onboarding';
import PartnerInstance from './PartnerInstance';
import PartnerPrograms from './PartnerPrograms';
import Payment from './Payment';
import Post from './Post';
import Product from './Product/index';
import Scheduler from './Scheduler';
import Section from './Section';
import Survey from './Survey';
import TeacherAssistant from './TeacherAssistant';
import Team from './Team';
import University from './University';
// eslint-disable-next-line import/no-cycle
import User from './User';
import UserConnection from './UserConnection';
import { getPartnerCookie } from 'utils/cookies';
import {
  getMembersFromResponse,
  prepareQueryParams,
  processAPIRequestPayload,
  processAPIRequestPayloadV2,
} from 'utils/apiHelpers';
import { isAuthenticated } from 'utils/customHooks/auth';
import JsonApi from 'utils/jsonApi';
import { getAppSettings } from 'utils/privateLabel/index';
import { getSessionStorageData } from 'utils/sessionStorage';
import { UTM_VALUES } from 'utils/heap';
import store from 'ducks/store';
import { DonationAskInterface } from 'commons/types/donationAskTypes';
import { TermInterface } from 'commons/types/termTypes';
import { EnrollmentInterface } from 'commons/types/enrollmentTypes';

import { LocationInterface } from 'commons/types/locationTypes';
import { LMSInstanceInterface } from 'commons/types/lmsInstanceTypes';
import { GetPaginatedRequestInterface, GetPaginatedResponseInterface } from 'commons/types/api';

export const axios = Axios.create({
  baseURL: apiUrl.base,
});

axios.interceptors.request.use(async (req) => {
  const { getState } = store;
  const { partnerInstance } = getState();
  const appSettings = getAppSettings();

  if (!req.headers['X-Noodle-Partner-Id']) {
    req.headers['X-Noodle-Partner-Id'] = (
      appSettings || partnerInstance.globalPartner.data
    )?.partnerId;
  }

  const partnerId = partnerInstance?.globalPartner?.data?.partnerId;
  const token = getPartnerCookie('access_token', partnerId);

  if (token && isAuthenticated(partnerId)) {
    req.headers.Authorization = `Bearer ${token}`;
  }

  return req;
});

const jsonApi = new JsonApi(axios);

const queryBaseSettings = {
  baseUrl: apiUrl.base,
  prepareHeaders: (headers, { getState }) => {
    const queryParams = new URLSearchParams(window.location.search);

    const { partnerInstance }: any = getState();
    const appSettings = getAppSettings();
    const partnerId = partnerInstance?.globalPartner?.data?.partnerId;

    const noodleCartSessionId =
      queryParams.get('cartSessionId') ||
      getPartnerCookie(NOODLE_CART_SESSION_ID_COOKIE_KEY, partnerId);

    if (!headers.get('X-Noodle-Partner-Id')) {
      headers.set(
        'X-Noodle-Partner-Id',
        `${appSettings?.partnerId || partnerInstance.globalPartner.data?.partnerId}`,
      );
    }

    if (!headers.get('X-Noodle-Cart-Session-Id') && noodleCartSessionId) {
      headers.set('X-Noodle-Cart-Session-Id', noodleCartSessionId);
    }

    const token = getPartnerCookie('access_token', partnerId);

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  },
};

interface VerifyInviteTokenResponse {
  id: number;
  returnUrl: string;
  token: string;
  partnerId: string;
}

// RTK-Query recommends only one createApi across the whole app if possible.
// That would mean adding all API calls in this file.
// RTK-Query will handle the state almost entirely, there's no need to create
// new reducers, actions or sagas unless we need to persist specific data,
// then we can create a slice.
export const rtkQApi = createApi({
  reducerPath: 'rtkQApi',
  baseQuery: fetchBaseQuery(queryBaseSettings),
  tagTypes: [
    'ChildInstancesCourseStatus',
    'Course',
    'CourseGroups',
    'CoursesFromNetwork',
    'DonationAsks',
    'Invites',
    'NetworkCourses',
    'OwnUserStripeCustomer',
    'PaginatedContentProviders',
    'PaginatedCourseSections',
    'PaginatedInstructors',
    'PaginatedMembershipSubscriptions',
    'PaginatedSectionLearners',
    'Partner',
    'PartnerPaidSeatPrice',
    'Section',
    'ShoppingCart',
    'StripeInvoice',
    'CourseWaitlist',
    'UsersCourseWaitlists',
    'OwnUserStripeCustomer',
    'DonationAsks',
    'PaginatedSectionLearners',
    'PaginatedCourseSections',
    'PaginatedInstructors',
    'PaginatedContentProviders',
    'PaginatedDonationAsks',
    'PaginatedCourses',
    'Courses',
    'PaginatedCourseCategories',
    'PaginatedChildPartners',
    'PartnerCourses',
    'Courses',
    'Roles',
    'Team',
    'Terms',
    'PaginatedLocations',
    'UsersCourseWaitlists',
    'CourseWaitlist',
    'Enrollments',
    'LMSInstances',
    'Waivers',
    'PaginatedUserCourses',
  ],
  endpoints: (builder) => ({
    getChildInstancesCourseStatus: builder.query<any, string>({
      query: (courseContentfulId) => `courses/${courseContentfulId}/instances`,
      providesTags: ['ChildInstancesCourseStatus'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getPartnerPaidSeatPrice: builder.query<any, string>({
      query: (partnerId) => `partners/${partnerId}/stripe/seat_price`,
      providesTags: ['PartnerPaidSeatPrice'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    updateChildInstanceCourses: builder.mutation({
      query: ({ data, courseContentfulId, isOwnCourse, lms }) => ({
        url: `courses/${courseContentfulId}/instances`,
        method: 'POST',
        body: humps.decamelizeKeys({ data, isOwnCourse, lms }),
      }),
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
      invalidatesTags: ['ChildInstancesCourseStatus'],
    }),
    shareCourseToFedNetwork: builder.mutation<any, { slug: string; priceInCents: number }>({
      query: ({ slug, priceInCents }) => ({
        url: `courses/${slug}/share`,
        method: 'POST',
        body: { price_in_cents: priceInCents },
      }),
      invalidatesTags: ['Course'],
      transformResponse: (response: any, meta, { slug }) =>
        humps.camelizeKeys({ ...response?.data, slug }),
    }),
    unshareCourseFromFedNetwork: builder.mutation<any, { slug: string }>({
      query: ({ slug }) => ({
        url: `courses/${slug}/unshare`,
        method: 'POST',
      }),
      invalidatesTags: ['Course'],
      transformResponse: (response: any, meta, { slug }) =>
        humps.camelizeKeys({ ...response?.data, slug }),
    }),
    getNetworkCourses: builder.query<any, any>({
      query: ({ partnerId, pageSize = 10, actualPage }) => {
        const queryParams = new URLSearchParams({
          partnerId,
          'page[size]': pageSize.toString(),
          'page[number]': actualPage.toString(),
        });
        return `/v2/courses/federated_network/market?${queryParams.toString()}`;
      },
      providesTags: ['NetworkCourses'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    uploadAddLearnersCSV: builder.mutation<
      any,
      {
        file: File;
        partnerId: string;
        teamId?: string;
        slug?: string;
      }
    >({
      query: ({ file, partnerId, teamId, slug }) => {
        const formData = new FormData();
        formData.append('members', file);

        return {
          url:
            !slug && teamId
              ? `teams/${teamId}/members/upload_csv?partner_id=${partnerId}`
              : `teams/courses/${slug}/upload_csv?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getPartnerCoursePreview: builder.query<
      CourseType,
      {
        partnerId?: string;
        slug: string;
        isNetworkPreview?: boolean;
        excludeAttributes?: string;
        include?: string;
      }
    >({
      query: ({ partnerId, slug, isNetworkPreview, excludeAttributes, include }) => {
        const queryParams = new URLSearchParams();
        if (partnerId) {
          queryParams.append('partner_id', partnerId);
        }
        if (excludeAttributes) {
          queryParams.append('exclude_attributes', excludeAttributes);
        }
        if (include) {
          queryParams.append('include', include);
        }
        return isNetworkPreview
          ? `/v2/courses/${slug}/federated_network?${queryParams.toString()}`
          : `/v2/courses/${slug}?${queryParams.toString()}`;
      },
      providesTags: ['Course'],
      transformResponse: (response: any) =>
        humps.camelizeKeys({
          ...response?.data,
          instructorsCollection: response?.data?.instructorsCollection?.items || [],
        }),
    }),
    getCoursesFromNetwork: builder.query<any, { pageSize?: number; pageNumber?: number }>({
      query: ({ pageSize = 10, pageNumber }) => {
        const queryParams = new URLSearchParams({
          'page[size]': pageSize.toString(),
          'page[number]': pageNumber.toString(),
        });
        return `v2/courses/federated_network/added_from_network?${queryParams.toString()}`;
      },
      providesTags: ['CoursesFromNetwork'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    getTeamPreview: builder.query<any, any>({
      queryFn: async ({ teamId, userId, fetchAcceptedMembers }) => {
        const adminResponse = await axios.get(`/teams/${teamId}?fields[team]=user_id`);
        const adminResponseData = humps.camelizeKeys(adminResponse?.data);

        const acceptedMembers = fetchAcceptedMembers ? ',accepted_members' : '';
        const teamResponse = await axios.get(`/teams/${teamId}?include=admin${acceptedMembers}`);
        const teamResponseData = humps.camelizeKeys(teamResponse?.data);

        const team = get(teamResponseData, 'data.attributes', {});
        const userFlags: TeamUserFlags = get(teamResponseData, 'data.userFlags', {});

        const adminId = get(adminResponseData, `entities.team[${teamId}].userId`, -1);
        const userIsAdmin = adminId === Number(userId);
        return {
          data: {
            id: Number(teamId),
            ...team,
            ...getMembersFromResponse(teamResponseData, userIsAdmin),
            userFlags,
          },
        };
      },
      providesTags: ['Team'],
    }),
    getPartnerById: builder.query<any, { partnerId: string }>({
      query: ({ partnerId }) => `/partners/${partnerId}`,
      providesTags: ['Partner'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getSectionById: builder.query<SectionType, { sectionId: number }>({
      query: ({ sectionId }) => `/sections/${sectionId}`,
      providesTags: ['Section'],
      transformResponse: (response: any) =>
        humps.camelizeKeys({
          ...response?.data.attributes,
          id: response?.data?.id,
        }),
    }),
    getShoppingCart: builder.query<
      ShoppingCartInterface,
      { isTemporary: boolean; sessionId?: string }
    >({
      query: ({ isTemporary, sessionId = '' }) => {
        const queryParams = new URLSearchParams({
          is_temporary: isTemporary.toString(),
        });
        if (sessionId) {
          queryParams.append('session_id', sessionId);
        }
        return `/cart?${queryParams.toString()}`;
      },
      providesTags: ['ShoppingCart'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    addCartItemToShoppingCart: builder.mutation<
      CartItemInterface,
      {
        shoppingCartId: number;
        externalId: string;
        stripeProductId: string;
        quantity: number;
        cartItemMetadata: any;
        itemType: string;
        donationAskId?: number;
        courseId?: number;
      }
    >({
      query: ({
        externalId,
        quantity,
        cartItemMetadata,
        shoppingCartId,
        stripeProductId,
        itemType,
        courseId = null,
        donationAskId = null,
      }) => ({
        url: `/cart/${shoppingCartId}/cart_items`,
        method: 'POST',
        body: {
          external_id: externalId,
          course_id: courseId,
          donation_ask_id: donationAskId,
          stripe_product_id: stripeProductId,
          quantity,
          cart_item_metadata: humps.decamelizeKeys(cartItemMetadata),
          item_type: itemType,
        },
      }),
      invalidatesTags: ['ShoppingCart', 'DonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    updateCartItemFromShoppingCart: builder.mutation<
      CartItemInterface | DeletedCartItemResponseInterface,
      { shoppingCartId: number; cartItemId: number; quantity?: number; isDepositPayment?: boolean }
    >({
      query: ({ shoppingCartId, cartItemId, quantity = null, isDepositPayment = null }) => ({
        url: `/cart/${shoppingCartId}/cart_items/${cartItemId}`,
        method: 'PATCH',
        body: {
          quantity,
          is_deposit_payment: isDepositPayment,
        },
      }),
      invalidatesTags: ['ShoppingCart'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    deleteCartItemFromShoppingCart: builder.mutation<
      CartItemInterface | DeletedCartItemResponseInterface,
      { shoppingCartId: number; cartItemId: number }
    >({
      query: ({ shoppingCartId, cartItemId }) => ({
        url: `/cart/${shoppingCartId}/cart_items/${cartItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ShoppingCart', 'DonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    deleteCouponFromShoppingCart: builder.mutation<
      DeletedCouponResponseInterface,
      { shoppingCartId: number; customerPromotionCode: string }
    >({
      query: ({ shoppingCartId, customerPromotionCode }) => ({
        url: `/cart/${shoppingCartId}/coupons/${customerPromotionCode}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ShoppingCart'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    clearShoppingCart: builder.mutation<any, { shoppingCartId: number }>({
      query: ({ shoppingCartId }) => ({
        url: `/cart/${shoppingCartId}/cart_items`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ShoppingCart'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    createInvoiceFromShoppingCart: builder.mutation<
      ShoppingCartInvoiceInterface,
      { shoppingCartId: number; stripeCustomerId: string }
    >({
      query: ({ shoppingCartId, stripeCustomerId }) => {
        const utmParams = getSessionStorageData(UTM_VALUES);

        return {
          url: `/cart/${shoppingCartId}/stripe/invoice`,
          method: 'POST',
          body: {
            utm_parameters: utmParams,
            stripe_customer_id: stripeCustomerId,
          },
        };
      },
      invalidatesTags: ['ShoppingCart', 'StripeInvoice', 'DonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    completeShoppingCartCheckout: builder.mutation<
      ShoppingCartCheckoutInterface,
      { shoppingCartId: number; invoiceId: string }
    >({
      query: ({ shoppingCartId, invoiceId }) => ({
        url: `/cart/${shoppingCartId}/checkout`,
        method: 'POST',
        body: {
          invoice_id: invoiceId,
        },
      }),
      invalidatesTags: ['ShoppingCart', 'DonationAsks', 'PaginatedUserCourses'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    applyCouponToShoppingCart: builder.mutation<
      CartItemInterface,
      { shoppingCartId: number; couponCode: string }
    >({
      query: ({ shoppingCartId, couponCode }) => ({
        url: `/cart/${shoppingCartId}/apply_coupon`,
        method: 'POST',
        body: {
          promotion_code: couponCode,
        },
      }),
      invalidatesTags: ['ShoppingCart'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getCourseWaitlist: builder.query<
      CourseWaitlistInterface,
      { courseContentfulId?: string; courseId?: number }
    >({
      query: ({ courseContentfulId = null, courseId = null }) => {
        const queryParams = new URLSearchParams();
        if (courseContentfulId) {
          queryParams.append('course_contentful_id', courseContentfulId);
        }
        if (courseId) {
          queryParams.append('course_id', courseId.toString());
        }
        return {
          url: `/course_waitlist?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['CourseWaitlist'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getUsersCourseWaitlistRecords: builder.query<
      UsersCourseWaitlistsInterface,
      { courseContentfulIds?: string[]; courseIds?: number[] }
    >({
      query: ({ courseContentfulIds, courseIds }) => {
        const queryParams = new URLSearchParams();
        if (courseContentfulIds) {
          queryParams.append('course_contentful_ids', courseContentfulIds.toString());
        }
        if (courseIds) {
          queryParams.append('course_ids', courseIds.toString());
        }
        return `/course_waitlist/my_waitlists?${queryParams.toString()}`;
      },
      providesTags: ['UsersCourseWaitlists'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    addUserToCourseWaitlist: builder.mutation<
      CourseWaitlistInterface,
      {
        courseId?: number;
        courseContentfulId?: string;
        userId: number;
      }
    >({
      query: ({ courseId = null, courseContentfulId = null, userId }) => {
        const queryParams = new URLSearchParams();
        if (courseId) {
          queryParams.set('course_id', courseId.toString());
        }
        if (courseContentfulId) {
          queryParams.set('course_contentful_id', courseContentfulId);
        }
        return {
          url: `/course_waitlist?${queryParams.toString()}`,
          method: 'POST',
          body: {
            users: [{ user_id: userId, add_or_remove_user: CourseWaitlistOperationEnum.Add }],
          },
        };
      },
      invalidatesTags: ['CourseWaitlist', 'UsersCourseWaitlists'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    addUsersToCourseWaitlist: builder.mutation<
      CourseWaitlistInterface,
      {
        courseContentfulId?: string;
        courseId: number;
        users: AddOrRemoveUsersInterface;
      }
    >({
      query: ({ courseContentfulId = null, courseId = null, users }) => {
        const queryParams = new URLSearchParams();
        if (courseId) {
          queryParams.set('course_id', courseId.toString());
        }
        if (courseContentfulId) {
          queryParams.set('course_contentful_id', courseContentfulId);
        }
        return {
          url: `/course_waitlist?${queryParams.toString()}`,
          method: 'POST',
          body: {
            users,
          },
        };
      },
      invalidatesTags: ['CourseWaitlist', 'UsersCourseWaitlists'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    removeUserFromCourseWaitlist: builder.mutation<
      CourseWaitlistInterface,
      {
        courseContentfulId?: string;
        courseId?: number;
        userId: number;
      }
    >({
      query: ({ courseContentfulId = null, courseId = null, userId }) => {
        const queryParams = new URLSearchParams();
        if (courseId) {
          queryParams.set('course_id', courseId.toString());
        }
        if (courseContentfulId) {
          queryParams.set('course_contentful_id', courseContentfulId);
        }
        return {
          url: `/course_waitlist?${queryParams.toString()}`,
          method: 'POST',
          body: {
            users: [{ user_id: userId, add_or_remove_user: CourseWaitlistOperationEnum.Remove }],
          },
        };
      },
      invalidatesTags: ['CourseWaitlist', 'UsersCourseWaitlists'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    removeUsersFromCourseWaitlist: builder.mutation<
      CourseWaitlistInterface,
      {
        courseContentfulId?: string;
        courseId?: string;
        users: AddOrRemoveUsersInterface;
      }
    >({
      query: ({ courseContentfulId = null, courseId = null, users }) => {
        const queryParams = new URLSearchParams();
        if (courseId) {
          queryParams.set('course_id', courseId.toString());
        }
        if (courseContentfulId) {
          queryParams.set('course_contentful_id', courseContentfulId);
        }
        return {
          url: `/course_waitlist?${queryParams.toString()}`,
          method: 'POST',
          body: {
            users,
          },
        };
      },
      invalidatesTags: ['CourseWaitlist', 'UsersCourseWaitlists'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    getPartnerStripeInvoiceById: builder.query<
      InvoiceInterface,
      {
        partnerId: string;
        invoiceId: string;
        isFederatedNetwork: boolean;
        publisherPartnerId: string;
      }
    >({
      query: ({ partnerId, invoiceId, isFederatedNetwork, publisherPartnerId }) => {
        const queryParams = new URLSearchParams();

        if (isFederatedNetwork) {
          queryParams.set('is_federated_network_course', String(isFederatedNetwork));
          queryParams.set('publisher_partner_id', publisherPartnerId);
        }

        return `/partners/${partnerId}/stripe/invoices/${invoiceId}?${
          queryParams ? queryParams.toString() : ''
        }`;
      },
      providesTags: ['StripeInvoice'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    /* This doesn't take into account admin purchases, this is solely for user self enrollments */
    createStripeCourseInvoice: builder.mutation<
      CreateStripeCourseInvoiceResponse,
      {
        partnerId: string;
        courseSlug: string;
        isAddedFromFedNet: boolean;
        publisherPartnerId: string;
        stripeCustomerId: string;
        quantity?: number;
      }
    >({
      query: ({
        partnerId,
        courseSlug,
        isAddedFromFedNet,
        publisherPartnerId,
        stripeCustomerId,
        quantity = 1,
      }) => {
        const queryParams = new URLSearchParams();
        const utmParams = getSessionStorageData(UTM_VALUES);

        if (isAddedFromFedNet) {
          queryParams.set('is_federated_network_course', String(isAddedFromFedNet));
          queryParams.set('publisher_partner_id', publisherPartnerId);
        }
        return {
          url: `/partners/${partnerId}/stripe/invoices/courses?${
            queryParams ? queryParams.toString() : ''
          }`,
          method: 'POST',
          body: humps.decamelizeKeys({
            courseSlug,
            quantity,
            isAdminEnrollment: false,
            utmParameters: utmParams,
            stripeCustomerId,
          }),
        };
      },
      invalidatesTags: ['StripeInvoice'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getOwnUserStripeCustomer: builder.query<CustomerInterface, string | void>({
      query: (stripeAccountPartnerId = null) =>
        `/users/me/stripe/customers${
          stripeAccountPartnerId ? `?stripe_account_partner_id=${stripeAccountPartnerId}` : ''
        }`,
      providesTags: ['OwnUserStripeCustomer'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createOrUpdateOwnUserStripeCustomer: builder.mutation<
      CustomerInterface,
      {
        email: string;
        name: string;
        address: {
          line1: string;
          line2?: string;
          city: string;
          state?: string;
          postalCode?: string;
          country: string;
        };
        stripeAccountPartnerId?: string;
      }
    >({
      query: ({ email, name, address, stripeAccountPartnerId = null }) => ({
        url: `/users/me/stripe/customers${
          stripeAccountPartnerId ? `?stripe_account_partner_id=${stripeAccountPartnerId}` : ''
        }`,
        method: 'POST',
        body: humps.decamelizeKeys({
          email,
          name,
          address,
        }),
      }),
      invalidatesTags: ['OwnUserStripeCustomer'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateUserProfile: builder.mutation<
      void,
      {
        firstName: string;
        middleName: string;
        lastName: string;
        dateOfBirth: string;
        phoneNumber: string;
        address: string;
        postalCode: string;
        city: string;
        state: string;
        country: string;
      }
    >({
      query: ({
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        phoneNumber,
        address,
        postalCode,
        city,
        state,
        country,
      }) => ({
        url: `/users/profile`,
        method: 'PATCH',
        body: {
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          date_of_birth: dateOfBirth,
          phone_number: phoneNumber,
          address: {
            address,
            postal_code: postalCode,
            city,
            state,
            country,
          },
        },
      }),
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getDonationAsks: builder.query<DonationAskData[], { courseSlug?: string }>({
      query: ({ courseSlug }) => {
        const queryParams = new URLSearchParams();
        if (courseSlug) {
          queryParams.set('course_slug', courseSlug);
        }
        return `/v2/donation_asks?${queryParams ? queryParams.toString() : ''}`;
      },
      providesTags: ['DonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response?.data),
    }),
    resendMembershipEmails: builder.mutation<
      {
        sentInvitations: boolean;
      },
      { userIds?: string[]; bulkResendNeverLoggedInUsers?: boolean; partnerId: string }
    >({
      query: ({ userIds = [], bulkResendNeverLoggedInUsers = false, partnerId }) => {
        const queryParams = new URLSearchParams();
        queryParams.set('partner_id', partnerId);
        const requestPayload: { [key: string]: any } = {};

        if (userIds?.length > 0) {
          requestPayload.user_ids = userIds;
        } else if (bulkResendNeverLoggedInUsers) {
          requestPayload.bulk_resend_never_logged_in_users = bulkResendNeverLoggedInUsers;
        } else {
          return null;
        }

        return {
          url: `/partner-instance-memberships/resend-invitations${
            partnerId ? `?${queryParams.toString()}` : ''
          }`,
          method: 'POST',
          body: requestPayload,
        };
      },
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPaginatedSectionLearners: builder.query<
      any,
      {
        includeAttributes?: string;
        pageSize?: number;
        pageNumber?: number;
        sectionId: string;
        search?: string;
      }
    >({
      query: ({ includeAttributes = '', pageSize = 10, pageNumber, search = '', sectionId }) => {
        const queryParams = new URLSearchParams({
          'page[number]': pageNumber.toString(),
          'page[size]': pageSize.toString(),
          include: 'admin,accepted_members',
          search,
          include_attributes: includeAttributes,
        });
        return `/sections/${sectionId}?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedSectionLearners'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(
          response.included.map(({ attributes, id, type }) => ({ ...attributes, id, type })),
        ),
        meta: response.meta,
      }),
    }),
    getPaginatedCourseSections: builder.query<
      any,
      {
        slug: string;
        partnerId: string;
        pageSize?: number;
        pageNumber?: number;
        searchByAdmin?: string;
        getSoloLearners?: boolean;
      }
    >({
      query: ({
        slug,
        partnerId,
        pageNumber,
        pageSize = 10,
        getSoloLearners = false,
        searchByAdmin = '',
      }) => {
        const queryParams = new URLSearchParams({
          include: getSoloLearners ? 'solo-sections' : 'sections',
          partner_id: partnerId,
          search_by_admin: searchByAdmin,
          'page[size]': pageSize.toString(),
          'page[number]': pageNumber.toString(),
        });
        return `/v2/courses/${slug}?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedCourseSections'],
      transformResponse: (response: any) => ({
        data: response.data?.sections?.map((section) =>
          humps.camelizeKeys({
            ...section.attributes,
            id: section.id,
          }),
        ),
        meta: response.meta,
      }),
    }),
    deleteUserCourseEnrollment: builder.mutation<
      void,
      { userId: number; courseSlug: string; partnerId: string }
    >({
      query: ({ courseSlug, userId, partnerId }) => {
        const queryParams = new URLSearchParams({
          partnerId,
        });
        return {
          url: `/courses/${courseSlug}/users/${userId}?${queryParams.toString()}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['PaginatedSectionLearners', 'Course', 'PaginatedCourseSections'],
    }),
    getPaginatedInstructors: builder.query<
      GetPaginatedResponseInterface<InstructorInterface>,
      // TODO: getSoloLearners is not used in the query?
      GetPaginatedRequestInterface & { getSoloLearners?: boolean }
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '', fields = null }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          fields,
        });
        return `/v3/instructors?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedInstructors'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createInstructor: builder.mutation<
      InstructorInterface,
      { payload: InstructorInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = new FormData();
        const keys = Object.keys(payload);
        keys.forEach((key) => {
          if (payload[key]) {
            formData.append(humps.decamelize(key), payload[key]);
          }
        });

        return {
          url: `/v3/instructors?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedInstructors'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPaginatedDonationAsks: builder.query<
      GetPaginatedResponseInterface<DonationAskInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '' }) => {
        const queryParams = new URLSearchParams({
          partner_id: partnerId,
          search,
          per_page: perPage.toString(),
          page: page.toString(),
          sort,
        });
        return `/v3/donation_asks?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedDonationAsks'],
      transformResponse: (response: any) => {
        const transformedData = humps.camelizeKeys(response);
        transformedData.data = transformedData.data.map((donationAsk) => ({
          ...donationAsk,
          priceInCents: donationAsk.priceInCents / 100,
        }));

        return transformedData;
      },
    }),
    createDonationAsk: builder.mutation<
      DonationAskInterface,
      { payload: DonationAskInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        if (payload.courses === '') {
          delete payload.courses;
        }
        const formData = processAPIRequestPayload<DonationAskInterface>(payload);

        return {
          url: `/v3/donation_asks?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedDonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getUnsplashImages: builder.query<any, { searchTerm: string; pageNumber: number }>({
      query: ({ searchTerm, pageNumber }) => ({
        url: `/unsplash/images?filter=[{"name": "search", "op": "eq", "val": "${searchTerm}"}]&page[number]=${pageNumber}&page[size]=30`,
      }),
      transformResponse: (response: any, _, { pageNumber }) => ({
        coverImages: humps.camelizeKeys(
          response?.data?.map(({ attributes, id }) => ({ ...attributes, id })),
        ),
        pageNumber,
      }),
    }),
    getPaginatedContentProviders: builder.query<
      GetPaginatedResponseInterface<ContentProviderInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '', fields = null }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          fields,
        });
        return `/v3/content_providers?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedContentProviders'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateInstructor: builder.mutation<
      InstructorInterface,
      { payload: InstructorInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<InstructorInterface>(payload);

        return {
          url: `/v3/instructors/${payload.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedInstructors'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateDonationAsk: builder.mutation<any, { payload: DonationAskInterface; partnerId: string }>({
      query: ({ payload, partnerId }) => {
        if (payload.courses === '') {
          delete payload.courses;
        }
        const formData = processAPIRequestPayload<DonationAskInterface>(payload);
        return {
          url: `/v3/donation_asks/${payload.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedDonationAsks'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteInstructor: builder.mutation<any, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/instructors/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaginatedInstructors'],
    }),
    deleteDonationAsk: builder.mutation<any, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/donation_asks/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaginatedDonationAsks'],
    }),
    createContentProvider: builder.mutation<
      ContentProviderInterface,
      { payload: ContentProviderInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = new FormData();
        const keys = Object.keys(payload);
        keys.forEach((key) => {
          const value = payload[key];

          if (value) {
            formData.append(key, value);
          }
        });

        return {
          url: `/v3/content_providers?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedContentProviders'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateContentProvider: builder.mutation<
      ContentProviderInterface,
      { payload: ContentProviderInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = new FormData();
        const keys = Object.keys(payload);
        keys.forEach((key) => {
          const value = payload[key];

          if (value) {
            formData.append(key, value);
          }
        });

        return {
          url: `/v3/content_providers/${payload?.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedContentProviders'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteContentProvider: builder.mutation<void, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/content_providers/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaginatedContentProviders'],
    }),
    getPaginatedCourses: builder.query<
      GetPaginatedResponseInterface<CourseType>,
      GetPaginatedRequestInterface
    >({
      query: ({
        partnerId,
        page,
        perPage = 10,
        search = '',
        sort = '',
        filters = null,
        fields = null,
      }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          filters,
          fields,
        });
        return `/v3/courses?${queryParams}`;
      },
      providesTags: ['PaginatedCourses'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    getCourseById: builder.query<
      { data: CourseType },
      { partnerId: string; courseId: string; fields?: string[] }
    >({
      query: ({ partnerId, courseId }) => {
        const queryParams = prepareQueryParams({
          partnerId,
        });
        return `/v3/courses/${courseId}?${queryParams}`;
      },
      providesTags: ['Courses'],
      transformResponse: (response: any) => ({
        data: humps.camelizeKeys(response),
      }),
    }),
    getPaginatedCourseCategories: builder.query<
      GetPaginatedResponseInterface<CourseCategories>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '', filters = null }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          filters,
        });
        return `/v3/course_categories?${queryParams}`;
      },
      providesTags: ['PaginatedCourseCategories'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    createCourse: builder.mutation<Partial<CourseType>, { payload: CourseType; partnerId: string }>(
      {
        query: ({ payload, partnerId }) => {
          payload.partnerId = partnerId;
          const formData = processAPIRequestPayloadV2<CourseType>(payload);

          return {
            url: `/v3/courses?partner_id=${partnerId}`,
            method: 'POST',
            body: formData,
            formData: true,
          };
        },
        invalidatesTags: ['PaginatedCourses'],
        transformResponse: (response: any) => humps.camelizeKeys(response),
      },
    ),
    updateCourse: builder.mutation<Partial<CourseType>, { payload: CourseType; partnerId: string }>(
      {
        query: ({ payload, partnerId }) => {
          const formData = new FormData();
          const keys = Object.keys(payload);
          keys.forEach((key) => {
            formData.append(key, payload[key]);
          });

          return {
            url: `/v3/courses/${payload.id}?partner_id=${partnerId}`,
            method: 'PUT',
            body: formData,
            formData: true,
          };
        },
        invalidatesTags: ['PaginatedCourses'],
        transformResponse: (response: any) => humps.camelizeKeys(response),
      },
    ),
    deletePartnerCourse: builder.mutation<Partial<CourseType>, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/courses/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaginatedCourses'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    publishCourse: builder.mutation<void, { partnerId: string; courseId: string }>({
      query: ({ partnerId, courseId }) => ({
        url: `/v3/courses/${courseId}/publish?partner_id=${partnerId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Courses', 'PaginatedCourses'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    republishCourse: builder.mutation<void, { payload: any; partnerId: string; courseId: string }>({
      query: ({ partnerId, courseId, payload }) => {
        const formData = processAPIRequestPayloadV2<CourseType>(payload);
        return {
          url: `/v3/courses/${courseId}/republish?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Courses', 'PaginatedCourses'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    uploadCourseCSV: builder.mutation<
      any,
      {
        file: File;
        partnerId: string;
      }
    >({
      query: ({ file, partnerId }) => {
        const formData = new FormData();
        formData.append('courses', file);
        return {
          url: `/v3/courses/bulk_upload_csv?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Courses', 'PaginatedCourses'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPaginatedCourseGroups: builder.query<
      GetPaginatedResponseInterface<CourseGroup>,
      GetPaginatedRequestInterface
    >({
      query: ({
        partnerId,
        page,
        perPage = 10,
        search = '',
        sort = '',
        filters = null,
        usePartnerPk = '0',
      }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          filters,
          usePartnerPk,
        });
        return `/v3/course_groups?${queryParams.toString()}`;
      },
      providesTags: ['CourseGroups'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPaginatedTerms: builder.query<
      GetPaginatedResponseInterface<TermInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '' }) => {
        const queryParams = new URLSearchParams({
          partner_id: partnerId,
          search,
          per_page: perPage.toString(),
          page: page.toString(),
          sort,
        });
        return `/terms?${queryParams.toString()}`;
      },
      providesTags: ['Terms'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createTerm: builder.mutation<TermInterface, { payload: TermInterface; partnerId: string }>({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayloadV2<TermInterface>(payload);
        return {
          url: `/terms?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Terms'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateTerm: builder.mutation<TermInterface, { payload: TermInterface; partnerId: string }>({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayloadV2<TermInterface>(payload);
        return {
          url: `/terms/${payload.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Terms'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteTerm: builder.mutation<void, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/terms/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Terms'],
    }),
    getPaginatedInvites: builder.query<
      GetPaginatedResponseInterface<PartnerInvite>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '', filters = null }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          filters,
        });
        return `/v3/invites?${queryParams}`;
      },
      providesTags: ['Invites'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createInvite: builder.mutation<
      CreateInviteResponse,
      { payload: PartnerInvite; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<PartnerInvite>(payload);

        return {
          url: `/v3/invites?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Invites'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteInvite: builder.mutation<void, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/invites/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invites'],
    }),
    resendInviteEmail: builder.mutation<void, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/invites/${id}/resend-email?partner_id=${partnerId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Invites'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    verifyInviteToken: builder.mutation<
      VerifyInviteTokenResponse,
      { inviteToken: string; partnerId: string }
    >({
      query: ({ inviteToken, partnerId }) => ({
        url: `/v3/invites/token/${inviteToken}?partner_id=${partnerId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Invites'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    uploadInviteCSV: builder.mutation<
      any,
      {
        file: File;
        partnerId: string;
      }
    >({
      query: ({ file, partnerId }) => {
        const formData = new FormData();
        formData.append('invites', file);
        return {
          url: `/v3/invites/bulk_upload_csv?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Invites'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPartners: builder.query<
      GetPaginatedResponseInterface<PartnerInstanceType>,
      GetPaginatedRequestInterface & {
        childrenOnly?: string;
        withChildren?: string;
        hasExternalIdpEnabled?: boolean;
        isActive?: string;
      }
    >({
      query: ({
        partnerId,
        page,
        perPage = 10,
        search = '',
        sort = '',
        childrenOnly = '0',
        withChildren = '0',
        hasExternalIdpEnabled = null,
        isActive = '1',
      }) => {
        const queryParams = new URLSearchParams({
          partner_id: partnerId ?? '',
          search,
          per_page: perPage.toString(),
          page: page.toString(),
          sort,
          children_only: childrenOnly,
          with_children: withChildren,
          is_active: isActive,
        });

        if (hasExternalIdpEnabled != null) {
          queryParams.append('has_external_idp', hasExternalIdpEnabled.toString());
        }

        return `/v3/partners?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedChildPartners'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    getPaginatedLocations: builder.query<
      GetPaginatedResponseInterface<LocationInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '' }) => {
        const queryParams = new URLSearchParams({
          partner_id: partnerId,
          search,
          per_page: perPage.toString(),
          page: page.toString(),
          sort,
        });
        return `/v3/locations?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedLocations'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createLocation: builder.mutation<
      LocationInterface,
      { payload: LocationInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<LocationInterface>(payload);

        return {
          url: `/v3/locations?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedLocations'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateLocation: builder.mutation<
      LocationInterface,
      { payload: LocationInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<LocationInterface>(payload);

        return {
          url: `/v3/locations/${payload.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedLocations'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteLocation: builder.mutation<any, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/locations/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaginatedLocations'],
    }),
    getRoles: builder.query<any, void>({
      query: () => `/roles`,
      providesTags: ['Roles'],
      transformResponse: (response: any) => {
        const camelizedData = humps.camelizeKeys(response?.data);
        const roles = camelizedData.map((role: any) => ({
          id: role.id,
          name: role.attributes.name,
        }));

        return roles;
      },
    }),
    getPaginatedEnrollments: builder.query<
      GetPaginatedResponseInterface<EnrollmentInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ page, perPage = 10, search = '', sort = '', filters = null }) => {
        const queryParams = new URLSearchParams({
          // @ts-ignore
          search,
          per_page: perPage.toString(),
          page: page.toString(),
          sort,
          filters: filters ? JSON.stringify(filters) : filters,
        });
        return `v3/enrollments?${queryParams.toString()}`;
      },
      providesTags: ['Enrollments'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    updateEnrollment: builder.mutation<
      EnrollmentInterface,
      { payload: EnrollmentInterface; partnerId: string }
    >({
      query: ({ payload }) => ({
        url: `v3/enrollments`,
        method: 'PATCH',
        body: payload,
        formData: true,
      }),
      invalidatesTags: ['Enrollments'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createLMSInstance: builder.mutation<
      LMSInstanceInterface,
      { payload: LMSInstanceInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<LMSInstanceInterface>(payload);

        return {
          url: `/v3/lms_instances?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['LMSInstances'],
      transformResponse: (response: unknown): LMSInstanceInterface =>
        humps.camelizeKeys(response) as LMSInstanceInterface,
    }),
    updateLMSInstance: builder.mutation<
      LMSInstanceInterface,
      { payload: LMSInstanceInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<LMSInstanceInterface>(payload);

        return {
          url: `/v3/lms_instances/${payload.id}?partner_id=${partnerId}`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['LMSInstances'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    getPaginatedLMSInstances: builder.query<
      GetPaginatedResponseInterface<LMSInstanceInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({ partnerId, page, perPage = 10, search = '', sort = '', filters = null }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          page,
          perPage,
          search,
          sort,
          filters,
        });
        return `v3/lms_instances?${queryParams}`;
      },
      providesTags: ['LMSInstances'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    verifyLmsInstance: builder.mutation<
      LMSInstanceInterface,
      { lmsInstanceId: number; partnerId: string }
    >({
      query: ({ partnerId, lmsInstanceId }) =>
        `/v3/lms_instances/${lmsInstanceId}/verify?partner_id=${partnerId}`,
      invalidatesTags: ['LMSInstances'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    deleteLMSInstance: builder.mutation<any, { id: number; partnerId: string }>({
      query: ({ id, partnerId }) => ({
        url: `/v3/lms_instances/${id}?partner_id=${partnerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LMSInstances'],
    }),
    createPartnerInstance: builder.mutation<any, { payload: any }>({
      query: ({ payload }) => {
        const formData = processAPIRequestPayload<any>(payload);

        return {
          url: `/partners`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
    }),
    updatePartnerInstance: builder.mutation<any, { payload: any; partnerId: string }>({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayload<any>(payload);

        return {
          url: `/partners/${partnerId}`,
          method: 'PATCH',
          body: formData,
          formData: true,
        };
      },
    }),
    getUserPaginatedCourses: builder.query<
      GetPaginatedResponseInterface<CourseType>,
      GetPaginatedRequestInterface
    >({
      query: ({
        partnerId,
        userId,
        page,
        perPage = 10,
        search = '',
        sort = '',
        filters = null,
        fields = null,
        include = '',
      }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          userId,
          page,
          perPage,
          search,
          sort,
          filters,
          fields,
          include,
        });
        return `/v2/courses/my_courses?${queryParams}`;
      },
      providesTags: ['PaginatedUserCourses'],
      transformResponse: (response: any) => ({
        ...response,
        data: humps.camelizeKeys(response?.data),
      }),
    }),
    getPaginatedMembershipSubscriptions: builder.query<
      GetPaginatedResponseInterface<MembershipSubscriptionInterface>,
      GetPaginatedRequestInterface
    >({
      query: ({
        partnerId,
        userId,
        page,
        perPage = 10,
        search = '',
        sort = '',
        fields = null,
        filters = null,
      }) => {
        const queryParams = prepareQueryParams({
          partnerId,
          userId,
          page,
          perPage,
          search,
          sort,
          fields,
          filters,
        });
        return `v3/memberships?${queryParams.toString()}`;
      },
      providesTags: ['PaginatedMembershipSubscriptions'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
    createMembershipSubscription: builder.mutation<
      MembershipSubscriptionInterface,
      { payload: MembershipSubscriptionInterface; partnerId: string }
    >({
      query: ({ payload, partnerId }) => {
        const formData = processAPIRequestPayloadV2<MembershipSubscriptionInterface>(payload);

        return {
          url: `v3/memberships?partner_id=${partnerId}`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['PaginatedMembershipSubscriptions'],
      transformResponse: (response: any) => humps.camelizeKeys(response),
    }),
  }),
});

// Auto generated hooks that are created based on the endpoints on createApi.
export const {
  useGetChildInstancesCourseStatusQuery,
  useUpdateChildInstanceCoursesMutation,
  useShareCourseToFedNetworkMutation,
  useUnshareCourseFromFedNetworkMutation,
  useGetNetworkCoursesQuery,
  useGetPartnerCoursePreviewQuery,
  useGetCoursesFromNetworkQuery,
  useUploadAddLearnersCSVMutation,
  useGetTeamPreviewQuery,
  useGetPartnerPaidSeatPriceQuery,
  useGetPartnerByIdQuery,
  useGetSectionByIdQuery,
  useGetShoppingCartQuery,
  useAddCartItemToShoppingCartMutation,
  useUpdateCartItemFromShoppingCartMutation,
  useDeleteCartItemFromShoppingCartMutation,
  useCreateInvoiceFromShoppingCartMutation,
  useCompleteShoppingCartCheckoutMutation,
  useApplyCouponToShoppingCartMutation,
  useGetCourseWaitlistQuery,
  useGetUsersCourseWaitlistRecordsQuery,
  useAddUserToCourseWaitlistMutation,
  useRemoveUserFromCourseWaitlistMutation,
  useAddUsersToCourseWaitlistMutation,
  useRemoveUsersFromCourseWaitlistMutation,
  useDeleteCouponFromShoppingCartMutation,
  useClearShoppingCartMutation,
  useGetPartnerStripeInvoiceByIdQuery,
  useCreateOrUpdateOwnUserStripeCustomerMutation,
  useGetOwnUserStripeCustomerQuery,
  useUpdateUserProfileMutation,
  useCreateStripeCourseInvoiceMutation,
  useGetDonationAsksQuery,
  useResendMembershipEmailsMutation,
  useDeleteUserCourseEnrollmentMutation,
  useGetPaginatedSectionLearnersQuery,
  useGetPaginatedCourseSectionsQuery,
  useGetPaginatedInstructorsQuery,
  useLazyGetPaginatedInstructorsQuery,
  useGetPaginatedCourseGroupsQuery,
  useLazyGetPaginatedCourseGroupsQuery,
  useCreateInstructorMutation,
  useLazyGetUnsplashImagesQuery,
  useGetPaginatedContentProvidersQuery,
  useLazyGetPaginatedContentProvidersQuery,
  useUpdateInstructorMutation,
  useDeleteInstructorMutation,
  useDeleteContentProviderMutation,
  useGetPaginatedDonationAsksQuery,
  useCreateDonationAskMutation,
  useUpdateDonationAskMutation,
  useDeleteDonationAskMutation,
  useCreateContentProviderMutation,
  useGetPaginatedCoursesQuery,
  useGetCourseByIdQuery,
  useLazyGetCourseByIdQuery,
  useGetPaginatedCourseCategoriesQuery,
  useLazyGetPaginatedCourseCategoriesQuery,
  useLazyGetPaginatedCoursesQuery,
  useCreateCourseMutation,
  useDeletePartnerCourseMutation,
  usePublishCourseMutation,
  useRepublishCourseMutation,
  useUploadCourseCSVMutation,
  useGetPartnersQuery,
  useLazyGetPartnersQuery,
  useGetPaginatedTermsQuery,
  useCreateTermMutation,
  useUpdateTermMutation,
  useDeleteTermMutation,
  useCreateInviteMutation,
  useResendInviteEmailMutation,
  useGetRolesQuery,
  useVerifyInviteTokenMutation,
  useGetPaginatedEnrollmentsQuery,
  useUploadInviteCSVMutation,
  useGetPaginatedLMSInstancesQuery,
  useCreateLMSInstanceMutation,
  useUpdateLMSInstanceMutation,
  useVerifyLmsInstanceMutation,
  useDeleteLMSInstanceMutation,
  useGetPaginatedLocationsQuery,
  useCreatePartnerInstanceMutation,
  useUpdatePartnerInstanceMutation,
  useGetPaginatedMembershipSubscriptionsQuery,
  useCreateMembershipSubscriptionMutation,
} = rtkQApi;

export default {
  adminGeneral: new AdminGeneral(jsonApi),
  adminNoodling: new AdminNoodling(jsonApi),
  adminUser: new AdminUser(jsonApi),
  adminScheduler: new AdminScheduler(jsonApi),
  adminPartnerProgram: new AdminPartnerProgram(jsonApi),
  bookmark: new Bookmark(jsonApi),
  counselor: new Counselor(jsonApi),
  explore: new Explore(jsonApi),
  getStream: new GetStream(jsonApi),
  globalSearch: new GlobalSearch(jsonApi),
  group: new Group(jsonApi),
  landingPage: new LandingPage(jsonApi),
  like: new Like(jsonApi),
  noodling: new Noodling(jsonApi),
  onboarding: new Onboarding(jsonApi),
  payment: new Payment(jsonApi),
  partnerPrograms: new PartnerPrograms(jsonApi),
  post: new Post(jsonApi),
  scheduler: new Scheduler(jsonApi),
  team: new Team(jsonApi),
  section: new Section(jsonApi),
  university: new University(jsonApi),
  user: new User(jsonApi),
  userConnection: new UserConnection(jsonApi),
  survey: new Survey(jsonApi),
  course: new Course(jsonApi),
  product: new Product(jsonApi),
  teacherAssistant: new TeacherAssistant(jsonApi),
  partnerInstance: new PartnerInstance(jsonApi),
};
